import React from 'react';
import { FormProvider } from 'react-hook-form';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import {
  Button,
  DashedTitle,
  Icon,
  InfoMessage,
  PageTitle,
  RequiredFieldsNotice
} from '@fasstech/middle-design-system';
import useSimulationDemographyForm from './hooks/useSimulationDemographyForm.js';
import DemographyBlock from './components/DemographyBlock.jsx';
import { not } from 'ramda';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import { ControlledRadioButton } from '@@viewCommonComponents/controlled/index.js';
import { isRequiredSelect } from '@@viewHelpers/validations/index.js';

const SimulationDemographyForm = ({
  getDemography,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  regimeObligatoire,
  coverageZones
}) => {
  const {
    form,
    fields,
    hasNoOffer,
    offerCheckFailed,
    loadingOffer,
    coverageZoneFlags,
    zoneOptionsAt,
    addZoneIsDisabled,
    canRemoveZone,
    handleAddZone,
    handleRemoveZone,
    $onPrevClick,
    isValid,
    collegeOptions,
    employeeCountTooltipText,
    spouseCountTooltipText,
    childrenCountTooltipText,
    employeeAverageAgeTooltipText,
    onSubmit
  } = useSimulationDemographyForm(
    getDemography,
    getMiddleText,
    getMiddleNumber,
    getMiddleBoolean,
    coverageZones
  );

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout
          withFormLayout={false}
          className={'flex flex-col items-center w-full gap-6'}
        >
          <div className={'flex flex-col items-center gap-6'}>
            <Icon name={'circleDemography'} size={70} />
            <PageTitle>
              {getMiddleText('ENTREPRISE_DEMOGRAPHIE_TITLE_BLOC')}
            </PageTitle>
            <RequiredFieldsNotice />
            <DashedTitle>
              {getMiddleText('ENTREPRISE_DEMOGRAPHIE_TITLE_SOUS_BLOC')}
            </DashedTitle>
            <div className={'p-4 w-full'}>
              <ControlledRadioButton
                name="college"
                label={getMiddleText('ENTREPRISE_DEMOGRAPHIE_COLLEGE_LABEL')}
                options={collegeOptions}
                form={form}
                className={'whitespace-nowrap py-4 mb-4'}
                data-testid="collegeChoice"
                rules={isRequiredSelect(collegeOptions, true)}
              />
              {coverageZoneFlags.isDisplayed && (
                <div className={'flex justify-end mb-2'}>
                  <Button
                    variant={'outline'}
                    onClick={handleAddZone}
                    disabled={addZoneIsDisabled}
                    data-testid="addCoverageZone"
                  >
                    {coverageZoneFlags.addButtonLabel}
                  </Button>
                </div>
              )}
              {fields.map((field, index) => (
                <DemographyBlock
                  key={field.uuid}
                  index={index}
                  form={form}
                  getMiddleText={getMiddleText}
                  getMiddleBoolean={getMiddleBoolean}
                  getMiddleNumber={getMiddleNumber}
                  regimeObligatoire={regimeObligatoire}
                  coverageZoneFlags={coverageZoneFlags}
                  zoneOptions={zoneOptionsAt(index)}
                  isRemovable={canRemoveZone(index)}
                  onRemove={() => handleRemoveZone(index)}
                  employeeCountTooltipText={employeeCountTooltipText}
                  spouseCountTooltipText={spouseCountTooltipText}
                  childrenCountTooltipText={childrenCountTooltipText}
                  employeeAverageAgeTooltipText={employeeAverageAgeTooltipText}
                />
              ))}
              {hasNoOffer && (
                <InfoMessage iconName="lightbulb">
                  {getMiddleText('ENTREPRISE_CCN_PAS_OFFRE_ASSUREUR_MESSAGE_INFO')}
                </InfoMessage>
              )}
            </div>
          </div>
        </PageLayout>
        <ButtonContainer
          $onPreviousButtonClick={$onPrevClick}
          nextButtonLabel={getMiddleText(
            'GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL'
          )}
          nextButtonIsDisabled={
            not(isValid) || hasNoOffer || offerCheckFailed || loadingOffer
          }
          isFormError={not(isValid)}
          getMiddleText={getMiddleText}
          displayResumeButton
        />
      </form>
    </FormProvider>
  );
};

export default SimulationDemographyForm;
